import axios, { AxiosError } from 'axios';
import type { OnErrorCallback } from '@bugsnag/js';

// https://docs.bugsnag.com/platforms/javascript/configuration-options/#onerror
// https://docs.bugsnag.com/platforms/javascript/customizing-error-reports/#discarding-events
const bugsnagOnErrorCallback: OnErrorCallback = (event) => {
  // Discard errors from crawler user agents detected by the server request.
  if (window.Clearstream?.global?.isCrawler) {
    return false;
  }

  const originalError = event.originalError;

  if (axios.isAxiosError(originalError)) {
    // Group Axios errors by their HTTP status code, if available.
    // https://docs.bugsnag.com/platforms/javascript/customizing-error-reports/#groupingdiscriminator
    if (originalError.response?.status) {
      event.setGroupingDiscriminator(
        `axios-status-code:${originalError.response.status}`,
      );
    }

    // Discard certain types of Axios errors.
    // https://github.com/axios/axios?tab=readme-ov-file#error-types
    if (
      // Feature or method is canceled explicitly by the user using an AbortSignal (or a CancelToken).
      originalError.code === AxiosError.ERR_CANCELED ||
      // The request has an unexpected format or is missing required parameters.
      // Usually related to a response with 4xx status code.
      originalError.code === AxiosError.ERR_BAD_REQUEST
    ) {
      return false;
    }

    // Network error with default message.
    if (
      originalError.code === AxiosError.ERR_NETWORK &&
      originalError.message === 'Network Error'
    ) {
      return false;
    }
  }

  if (
    originalError instanceof Error &&
    typeof originalError.stack === 'string'
  ) {
    // Discard certain errors originating from Wistia's external scripts, as they are not actionable by us.
    if (
      originalError.stack.includes(
        'https://fast.wistia.com/assets/external/',
      ) &&
      [
        `Cannot read properties of null (reading 'style')`,
        `can't access property "style", t is null`,
        `i.at is not a function. (In 'i.at(-1)', 'i.at' is undefined)`,
      ].includes(originalError.message)
    ) {
      return false;
    }
  }

  return true;
};

export default bugsnagOnErrorCallback;
